import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useFormik } from 'formik';
import { useState } from 'react';

import FormPersonalData from './FormPersonalData';
import NoTravelContact from './NoTravelContact';
import ContactInfo from './ContactInfo';
import EnteredData from './EnteredData';
import CoverageDetails from '../CoverageDetails';
import ContentSteps from './ContentSteps';

import {
  contactoNoviaja,
  envioVoucher,
  generateEmptyPassengers,
  generatePassangersArr,
} from './helpers';

import {
  setFormNoViaja,
  setFormPassengers,
  setFormContact,
} from '@shared/redux/actions';

import {
  ASSIST_PAYMENTS,
  ASSIST_PAYMENTS_SUCCESSFUL,
  ASSIST_SEARCH,
} from '@shared/helpers/routes';

import { postAssistEmit } from '@shared/services/services';
import { getUser } from '@shared/helpers/auth';

import GenericBookingLayout from '@shared/components/GenericBookingLayout';
import BoxContainer from '@shared/components/BoxContainer';
import { InfoDialogModal } from '@shared/components/Dialogs';

import { fullSchema } from './validationSchemas';

import { usePayeeSelection } from '@shared/components/PayeeSelector/usePayeeSelection';
import PayeeSelector from '@shared/components/PayeeSelector';
import PriceDetails from '../PriceDetails';

const AssistReserve = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = getUser();

  const { storageKey, elementSelected, datosDeBusqueda, xTransactionId } =
    location.state ?? {};

  const payee = usePayeeSelection();

  const [errorEmit, setErrorEmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = {
    passengers: generateEmptyPassengers({ elementSelected }),
    noViaja: contactoNoviaja,
    contact: envioVoucher,
    acceptedTerms: false,
    id_entity: payee.subsidiary?.id || '',
    id_assigned_seller: payee.seller?.id_user || '',
    isPointOfSale: payee.isPointOfSale,
    isEmit: Boolean(datosDeBusqueda?.emit),
  };

  const formik = useFormik({
    initialValues,
    validationSchema: fullSchema(elementSelected),
    context: {
      isPointOfSale: payee.isPointOfSale,
    },
    onSubmit: async values => {
      setIsLoading(true);

      if (datosDeBusqueda?.emit) {
        try {
          const body = {
            product_code: elementSelected.codigo,
            rate_code: elementSelected.codigoTarifa,
            start_date: elementSelected.start_date,
            end_date: elementSelected.end_date,
            passengers: generatePassangersArr(
              values.passengers,
              values.noViaja,
              values.contact.email
            ),
            id_transaction: elementSelected.id_transaction,
            id_user: user?.user.id || null,
            client_email: values.contact.email,
            agency_email: payee.subsidiary?.email,
            family_plan: datosDeBusqueda?.family_plan,
            destination: datosDeBusqueda?.destination.code,
            id_assigned_seller: payee.seller?.id_user,
            id_entity: payee.subsidiary?.id,
            comments: '-',
          };

          const resultado = await postAssistEmit(body, xTransactionId);

          history.push({
            pathname: ASSIST_PAYMENTS_SUCCESSFUL,
            state: {
              cardData: elementSelected,
              resultado,
              salesResult: {},
              state: {
                ...location.state,
                ...values,
                id_transaction: location.state.elementSelected.id_transaction,

                storagePassengers: elementSelected,
              },
              subsidiary: payee.subsidiary,
              seller: payee.seller,
            },
          });
        } catch (e) {
          console.error('formik submit assist book', e);

          const code = e.response?.data?.code;

          if (code === 'ISSUANCE_ALREADY_DONE') {
            history.push({
              pathname: ASSIST_PAYMENTS_SUCCESSFUL,
              state: {
                cardData: elementSelected,
                code,
                salesResult: {},
                state: {
                  ...location.state,
                  ...values,
                  id_transaction: location.state.elementSelected.id_transaction,
                  storagePassengers: elementSelected,
                },
                subsidiary: payee.subsidiary,
                seller: payee.seller,
              },
            });

            return;
          }

          setErrorEmit(true);

          const message =
            e.response?.data?.message || 'Ocurrió un error inesperado';

          setErrorMessage(message);
        } finally {
          setIsLoading(false);
        }
      } else {
        dispatch(setFormPassengers({ pasajeros: values.passengers }));
        dispatch(setFormNoViaja({ noViaja: values.noViaja }));
        dispatch(setFormContact({ contact: values.contact }));

        history.push({
          pathname: ASSIST_PAYMENTS,
          state: {
            storageKey,
            ...location.state,
            id_transaction: location.state.elementSelected.id_transaction,
            ...values,
            storagePassengers: location.state.elementSelected,
          },
        });
      }
    },
  });

  return (
    <GenericBookingLayout
      operationType={datosDeBusqueda?.emit ? 'reserva' : 'compra'}
      searchPath={ASSIST_SEARCH}
      storageKey={storageKey}
      stepsWrapperComponent={<ContentSteps searchData={datosDeBusqueda} />}
      formComponent={
        <Box>
          {formik.values.passengers.map((_, i) => (
            <FormPersonalData
              key={`assist-person-data-${+i}`}
              index={i}
              formik={formik}
              elementSelected={elementSelected}
            />
          ))}

          <ContactInfo formik={formik} />
          <NoTravelContact formik={formik} />

          {datosDeBusqueda?.emit && (
            <PayeeSelector payee={payee} formik={formik} />
          )}

          <EnteredData
            formik={formik}
            isLoading={isLoading}
            searchData={datosDeBusqueda}
            copyPassenger={formik.values.passengers}
          />
        </Box>
      }
      detailsComponent={
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <BoxContainer>
              <CoverageDetails storagePassengers={elementSelected} />
            </BoxContainer>
            <BoxContainer>
              <PriceDetails
                storagePassengers={elementSelected}
                total={elementSelected?.price?.details_USD?.total}
                hasCharges={!!elementSelected?.price?.details?.charges}
              />
            </BoxContainer>
          </Box>

          {errorEmit && (
            <InfoDialogModal
              isOpen={errorEmit}
              onClose={() => setErrorEmit(false)}
              title="Ocurrió un error al emitir"
              text={errorMessage || 'Intente nuevamente'}
            />
          )}
        </>
      }
    />
  );
};

export default AssistReserve;
